import React, { Fragment, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrashAlt,
  faEdit,
  faPlus,
  faSearch,
  faCheckCircle,
  faInfoCircle,
  faPhone,
  faUser,
  faLink,
  faExternalLinkAlt,
  faCloudUploadAlt,
  faTimes,
  faImage,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../../api/api";
import { Dialog, Transition } from "@headlessui/react";
import {
  Autocomplete,
  GoogleMap,
  Marker,
  useLoadScript,
} from "@react-google-maps/api";
import { PartnerPinLocationIcon } from "../../config/googlemapstyle";
import { GoogleMapStyle } from "../../config/constants";
import OperatingHoursInput from "../../components/OperatingHoursInput";
import ReactPhoneInput from "react-phone-input-2"; // Import the phone input component
import "react-phone-input-2/lib/style.css";
import LoadingLogo from "../../components/Loading";

const Partner = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editPartner, setEditPartner] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [meta, setMeta] = useState({
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
    from: 1,
    to: 1,
  });

  // Fetch partners and allergies on mount
  useEffect(() => {
    fetchPartners();
  }, [filter, currentPage]);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/partners`, {
        params: {
          search: searchTerm,
          filter,
          page: currentPage,
        },
      });
      setPartners(response.data);
      setMeta(response.meta);
      setLoading(false);
    } catch (error) {
      toast.error("Failed to fetch partners.");
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this partner?")) {
      try {
        await api.delete(`/partners/${id}`);
        toast.success("Partner deleted successfully.");
        fetchPartners();
      } catch (error) {
        toast.error("Failed to delete partner.");
      }
    }
  };

  const handleEdit = (partner) => {
    setEditPartner(partner);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setEditPartner(null);
    setIsModalOpen(true);
  };

  const handleSave = () => {
    setIsModalOpen(false);
    fetchPartners();
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleNextPage = () => {
    if (meta.current_page < meta.last_page) {
      setCurrentPage(meta.current_page + 1);
    }
  };

  const handlePrevPage = () => {
    if (meta.current_page > 1) {
      setCurrentPage(meta.current_page - 1);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= meta.last_page; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 rounded-lg border border-tblue ${i === meta.current_page
            ? "bg-tblue text-white"
            : "bg-white text-gray-700"
            } hover:bg-tblue hover:text-white mx-1`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  if (loading) {
    return <LoadingLogo />;
  }

  return (
    <div className="mx-auto px-4  max-w-full overflow-x-hidden">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 space-y-4 md:space-y-0">
        <h1 className="text-tblue text-3xl font-bold">Partners</h1>
        <button
          onClick={handleAdd}
          className="bg-primary text-white px-4 py-2 rounded-lg flex items-center hover:bg-accent"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Add New Partner
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-6">
        <div className="flex items-center border border-gray-300 rounded-lg p-2 shadow-md transition-all duration-300 hover:shadow-lg focus-within:shadow-2xl focus-within:bg-bodyMain w-full sm:w-auto">
          <FontAwesomeIcon
            icon={faSearch}
            className="text-gray-400 mr-2 transition-transform duration-300 hover:scale-110"
          />
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") fetchPartners();
            }}
            className="outline-none focus:ring-0 bg-transparent w-full text-gray-700 placeholder-gray-400 focus:placeholder-gray-500 focus:text-black"
          />
        </div>

        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="border rounded-lg p-2 shadow-sm w-full sm:w-auto"
        >
          <option value="all">All Partners</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div className="overflow-x-auto relative rounded-lg">
        <table className="min-w-full bg-white rounded-lg shadow-md text-sm sm:text-base">
          <thead className="bg-primary text-white">
            <tr>
              <th className="py-4 px-2 text-left sticky left-0 bg-primary z-2 border-r border-white">
                Image
              </th>
              <th className="py-4 px-2 text-left">Name</th>
              <th className="py-4 px-2 text-left">Active</th>
              <th className="py-4 px-2 text-left">Contact</th>
              <th className="py-4 px-2 text-left">Website</th>
              <th className="py-4 px-2 text-left">Description</th>
              <th className="py-4 px-2 text-left">Location</th>
              <th className="py-4 px-2 text-left">Hours</th>
              <th className="py-4 px-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {partners.length > 0 ? (
              partners.map((partner) => (
                <tr
                  key={partner.id}
                  className={`hover:bg-gray-100 ${!partner.is_active ? "bg-red-100" : ""
                    }`}
                >
                  <td className="py-2 px-2 font-semibold text-gray-700 whitespace-nowrap sticky left-0 bg-white z-2 border-r border-gray-200">
                    {partner.image_url ? (
                      <img
                        src={partner.image_url}
                        alt={partner.name}
                        className="w-10 h-10 object-cover rounded-full"
                      />
                    ) : (
                      <FontAwesomeIcon icon={faUser} className="text-gray-400" />
                    )}
                  </td>
                  <td className="py-2 px-2 font-semibold text-gray-700 whitespace-nowrap">
                    {partner.name}
                  </td>
                  <td className="py-2 px-2 text-center">
                    <span
                      className={`px-2 py-1 rounded-lg text-sm ${partner.is_active
                        ? "bg-green-100 text-green-600"
                        : "bg-red-100 text-red-600"
                        }`}
                    >
                      {partner.is_active ? "Yes" : "No"}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-gray-700 whitespace-nowrap">
                    {partner.contact || "-"}
                  </td>
                  <td className="py-2 px-2 whitespace-nowrap">
                    {partner.website ? (
                      <a
                        href={partner.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-700 flex items-center space-x-1"
                      >
                        <span>Visit</span>
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                      </a>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="py-2 px-2 text-gray-600">{partner.description || "-"}</td>
                  <td className="py-2 px-2 text-gray-700">
                    {partner.location?.address || "-"}
                  </td>
                  <td className="py-2 px-2 text-gray-600">
                    <div className="space-y-1">
                      {Object.entries(partner.hours || {}).map(([day, hours]) => (
                        <div key={day}>
                          <strong>{day}: </strong>
                          {hours.open ? `${hours.start} - ${hours.end}` : "Closed"}
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 px-2 text-center">
                    <div className="flex justify-center space-x-2">
                      <button
                        onClick={() => handleEdit(partner)}
                        className="text-blue-500 hover:text-blue-700"
                        title="Edit Partner"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        onClick={() => handleDelete(partner.id)}
                        className="text-red-500 hover:text-red-700"
                        title="Delete Partner"
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="py-4 text-center text-gray-500">
                  No partners available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <span className="text-gray-500 text-sm">
          Showing {meta.from} to {meta.to} of {meta.total} partners
        </span>

        <div className="flex items-center space-x-2">
          <button
            onClick={handlePrevPage}
            disabled={meta.current_page === 1}
            className={`bg-white px-4 py-2 rounded-lg border border-tblue ${meta.current_page === 1
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-gray-400"
              }`}
          >
            Previous
          </button>

          {renderPageNumbers()}

          <button
            onClick={handleNextPage}
            disabled={meta.current_page === meta.last_page}
            className={`bg-white px-4 py-2 rounded-lg border border-tblue ${meta.current_page === meta.last_page
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-gray-400"
              }`}
          >
            Next
          </button>
        </div>
      </div>

      {isModalOpen && (
        <CRUModal
          item={editPartner}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      )}
    </div>

  );
};

export default Partner;

const libraries = ["places"];

export const CRUModal = ({ item, onSave, onCancel }) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  const [formData, setFormData] = useState({
    name: "",
    is_active: true,
    location_address: "",
    location_latitude: null,
    location_longitude: null,
    description: "",
    contact: "",
    website: "",
    hours: "",
  });

  const [errors, setErrors] = useState({});
  const [autocompleteLocation, setAutocompleteLocation] = useState(null);

  // Image upload states
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);

  useEffect(() => {
    if (item) {
      const timeoutId = setTimeout(() => {
        if (item && item.location) {
          setFormData({
            ...item,
            location_address: item.location?.address || "",
            location_latitude: item.location?.latitude || null,
            location_longitude: item.location?.longitude || null,
            description: item.description || "",
            contact: item.contact || "",
            website: item.website || "",
            hours: item.hours || "",
          });

          // Set existing image preview if available
          if (item.media && item.media.length > 0) {
            setImagePreview(item.media[0].original_url);
          }
        }
      }, 1000); // 1 second delay

      // Clear timeout if the component unmounts before 1 second
      return () => clearTimeout(timeoutId);
    } else {
      // Reset image states for new partner
      setSelectedImage(null);
      setImagePreview(null);
      setErrors({});
    }
  }, [item]);

  // Image upload functions
  const handleImageSelect = (file) => {
    if (file && file.type.startsWith('image/')) {
      if (file.size > 6000 * 1024) { // 6000KB = 6MB
        toast.error('Image size must be less than 6MB');
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
      // Clear image error if exists
      if (errors.image) {
        setErrors(prev => ({ ...prev, image: null }));
      }
    } else {
      toast.error('Please select a valid image file');
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleImageSelect(file);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error for the field being edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handlePlaceSelect = () => {
    const place = autocompleteLocation.getPlace();
    const location = place.geometry?.location;
    if (location) {
      setFormData({
        ...formData,
        location_address: place.formatted_address,
        location_latitude: location.lat(),
        location_longitude: location.lng(),
      });
      // Clear location errors
      if (errors.location_address || errors.location_latitude) {
        setErrors(prev => ({
          ...prev,
          location_address: null,
          location_latitude: null,
          location_longitude: null
        }));
      }
    }
  };

  const handleLocationDragEnd = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        setFormData((prevData) => ({
          ...prevData,
          location_latitude: lat,
          location_longitude: lng,
          location_address: results[0].formatted_address,
        }));
      }
    });
  };

  const validate = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name || formData.name.trim() === "") {
      newErrors.name = "Name is required";
    }

    // Location validation
    if (!formData.location_latitude || !formData.location_longitude) {
      newErrors.location_address = "Location is required";
    }
    if (!formData.location_address || formData.location_address.trim() === "") {
      newErrors.location_address = "Location address is required";
    }

    // Contact validation: digits only, 7-15 length
    if (formData.contact) {
      const contactRegex = /^\d{7,15}$/;
      if (!contactRegex.test(formData.contact)) {
        newErrors.contact = "Contact must contain only digits and be between 7 and 15 digits long";
      }
    }

    // Description validation: max 1000 characters
    if (formData.description && formData.description.length > 1000) {
      newErrors.description = "Description cannot exceed 1000 characters";
    }

    // Website validation
    if (formData.website) {
      const urlPattern = /^(https?:\/\/)?(www\.)?([a-zA-Z0-9]+)\.([a-zA-Z]{2,})/;
      if (!urlPattern.test(formData.website)) {
        newErrors.website = "Please enter a valid URL";
      }
    }

    // Image validation
    if (!item && !selectedImage) {
      newErrors.image = "Partner image is required";
    }

    // Hours validation
    if (formData.hours) {
      const hoursErrors = {};
      let hasHoursErrors = false;
      let isAnyDayOpen = false;

      Object.entries(formData.hours).forEach(([day, hourData]) => {
        if (hourData.open) {
          isAnyDayOpen = true;
          if (!hourData.start || !hourData.end) {
            hoursErrors[day] = "Start and End time are required when open";
            hasHoursErrors = true;
          }
        }
      });

      if (!isAnyDayOpen) {
        newErrors.hours_general = "Please select at least one operating day.";
      } else if (hasHoursErrors) {
        newErrors.hours = hoursErrors;
      }
    } else {
      newErrors.hours_general = "Please set operating hours.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error("Please fix the errors before saving.");
      return;
    }

    setImageLoading(true);

    try {
      const formDataToSend = new FormData();

      // Add all form data
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          if (key === 'hours' && typeof formData[key] === 'object') {
            formDataToSend.append(key, JSON.stringify(formData[key]));
          } else {
            formDataToSend.append(key, formData[key]);
          }
        }
      });

      // Add image if selected
      if (selectedImage) {
        formDataToSend.append('image', selectedImage);
      }

      if (item) {
        formDataToSend.append('_method', 'PUT');
        await api.post(`/partners/${item.id}`, formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        toast.success("Partner updated successfully");
      } else {
        await api.post("/partners", formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        toast.success("Partner created successfully");
      }
      onSave();
    } catch (error) {
      // Handle backend validation errors
      if (error.response && error.response.status === 422 && error.response.data?.errors) {
        const backendErrors = error.response.data.errors;
        // Map backend errors to frontend error state where possible
        const mappedErrors = {};
        if (backendErrors.name) mappedErrors.name = backendErrors.name[0];
        if (backendErrors.contact) mappedErrors.contact = backendErrors.contact[0];
        if (backendErrors.website) mappedErrors.website = backendErrors.website[0];
        if (backendErrors.location_address) mappedErrors.location_address = backendErrors.location_address[0];
        if (backendErrors.image) mappedErrors.image = backendErrors.image[0];

        setErrors(prev => ({ ...prev, ...mappedErrors }));
        toast.error("Validation failed. Please check the inputs.");
      } else {
        toast.error("Error saving partner. Please check the form.");
      }
    } finally {
      setImageLoading(false);
    }
  };

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading Maps...</div>;

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onCancel}>
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900"
                >
                  {item ? "Edit Partner" : "Add New Partner"}
                </Dialog.Title>
                <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                  {/* Google Location Field */}
                  <div className="relative mt-20">
                    <div className="absolute -top-16 left-0 right-0 z-10 flex justify-center">
                      <div className="w-full sm:w-2/3 lg:w-2/3  p-4 rounded-lg shadow-lg backdrop-blur-md">
                        <div className="flex items-center justify-between space-x-4 mb-2">
                          <label className="block text-lg font-medium text-gray-900 mb-2">
                            Partner Location
                          </label>
                        </div>

                        <Autocomplete
                          onLoad={(ref) => setAutocompleteLocation(ref)}
                          onPlaceChanged={handlePlaceSelect}
                        >
                          <input
                            type="text"
                            placeholder="Search for location"
                            value={formData.location_address}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                location_address: e.target.value,
                              })
                            }
                            className={`block w-full p-3 border rounded-lg shadow-sm focus:ring-purple-600 focus:border-purple-600 transition ease-in-out duration-150 ${errors.location_address ? "border-red-500" : "border-gray-300"
                              }`}
                          />
                        </Autocomplete>
                        {errors.location_address && (
                          <p className="text-red-500 text-xs mt-1">{errors.location_address}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Google Map - Only show for steps 1 or 7 */}
                  <div className={`mt-10 bg-white shadow-lg rounded-lg `}>
                    <GoogleMap
                      center={
                        formData.location_latitude &&
                          formData.location_longitude
                          ? {
                            lat: formData.location_latitude,
                            lng: formData.location_longitude,
                          }
                          : { lat: 25.2048, lng: 55.2708 }
                      }
                      zoom={13}
                      mapContainerStyle={{
                        width: "100%",
                        height: "500px",
                        borderRadius: "12px",
                        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
                      }}
                      options={{
                        styles: GoogleMapStyle,
                        disableDefaultUI: true, // Hide default controls for a cleaner look
                        zoomControl: true, // Keep zoom controls
                      }}
                    >
                      {formData.location_latitude &&
                        formData.location_longitude && (
                          <Marker
                            position={{
                              lat: formData.location_latitude,
                              lng: formData.location_longitude,
                            }}
                            icon={{
                              url: PartnerPinLocationIcon,
                              scaledSize: new window.google.maps.Size(56, 76), // Ensure the icon is scaled to the right size
                            }}
                            draggable={true}
                            onDragEnd={handleLocationDragEnd}
                          />
                        )}
                    </GoogleMap>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <FontAwesomeIcon
                        icon={faUser}
                        className="mr-2 text-primary"
                      />
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`block w-full p-3 border rounded-lg focus:ring-primary focus:border-primary transition ${errors.name ? "border-red-500" : "border-gray-300"
                        }`}
                      placeholder="Enter partner name"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                    )}
                  </div>

                  {/* Partner Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FontAwesomeIcon
                        icon={faImage}
                        className="mr-2 text-purple-500"
                      />
                      Partner Image
                    </label>

                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Partner preview"
                          className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200 shadow-md"
                        />
                        <button
                          type="button"
                          onClick={removeImage}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors"
                          title="Remove image"
                        >
                          <FontAwesomeIcon icon={faTimes} className="text-xs" />
                        </button>
                      </div>
                    ) : (
                      <div
                        className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${isDragOver
                          ? 'border-purple-500 bg-purple-50'
                          : errors.image ? 'border-red-500 hover:border-red-600 hover:bg-red-50' : 'border-gray-300 hover:border-purple-400 hover:bg-gray-50'
                          }`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        <FontAwesomeIcon
                          icon={faCloudUploadAlt}
                          className={`text-4xl mb-4 ${errors.image ? 'text-red-400' : 'text-gray-400'}`}
                        />
                        <p className={`mb-2 ${errors.image ? 'text-red-600' : 'text-gray-600'}`}>
                          Drag and drop an image here, or{' '}
                          <label className="text-purple-500 hover:text-purple-600 cursor-pointer">
                            click to browse
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleFileInput}
                              className="hidden"
                            />
                          </label>
                        </p>
                        <p className="text-xs text-gray-500">
                          PNG, JPG, JPEG up to 6MB
                        </p>
                      </div>
                    )}
                    {errors.image && (
                      <p className="text-red-500 text-xs mt-1">{errors.image}</p>
                    )}
                  </div>

                  <div className="flex items-center mb-4">
                    <label className="block text-sm font-medium text-gray-700 mr-4 flex items-center">
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="mr-2 text-green-500"
                      />
                      Active
                    </label>
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          is_active: e.target.checked,
                        })
                      }
                      className="h-5 w-5 rounded border-gray-300 focus:ring-primary focus:border-primary"
                    />
                  </div>

                  {/* Description Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="mr-2 text-blue-500"
                      />
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      className={`block w-full p-3 border rounded-lg focus:ring-primary focus:border-primary transition ${errors.description ? "border-red-500" : "border-gray-300"
                        }`}
                      rows="3"
                      placeholder="Describe the partner, e.g., services offered, specialties"
                    />
                    {errors.description && (
                      <p className="text-red-500 text-xs mt-1">{errors.description}</p>
                    )}
                  </div>

                  {/* Contact Field with react-phone-input-2 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <FontAwesomeIcon
                        icon={faPhone}
                        className="mr-2 text-purple-500"
                      />
                      Contact
                    </label>
                    <ReactPhoneInput
                      country={"ae"} // Default to UAE
                      value={formData.contact}
                      onChange={(value) => {
                        setFormData({
                          ...formData,
                          contact: value,
                        });
                        // Clear contact error
                        if (errors.contact) {
                          setErrors(prev => ({ ...prev, contact: null }));
                        }
                      }}
                      inputStyle={{
                        width: "90%",
                        padding: "14px 20px",
                        border: `1px solid ${errors.contact ? "#ef4444" : "#d1d5db"}`,
                        borderRadius: "8px",
                        fontSize: "16px",
                        color: "#4b5563",
                        marginLeft: "45px", // Create space between flag and number input
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", // Subtle shadow for input
                        height: "50px", // Increase the overall height of the input for a spacious look
                      }}
                      buttonStyle={{
                        borderRadius: "8px 0 0 8px",
                        paddingRight: "10px", // Ensure the button has enough padding inside
                        height: "50px", // Match the height of the input
                        borderColor: errors.contact ? "#ef4444" : "#d1d5db"
                      }}
                      containerClass="mb-4"
                      inputClass="focus:ring-purple-500 focus:border-purple-500"
                      dropdownClass="phone-dropdown"
                      searchPlaceholder="Search for country" // Placeholder for search input
                      enableSearch={true} // Enable search functionality
                      searchStyle={{
                        padding: "10px 12px",
                        fontSize: "14px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                      }}
                    />
                    {errors.contact && (
                      <p className="text-red-500 text-xs mt-1" style={{ marginTop: '-12px', marginBottom: '10px' }}>{errors.contact}</p>
                    )}
                  </div>

                  {/* Website Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <FontAwesomeIcon
                        icon={faLink}
                        className="mr-2 text-blue-400"
                      />
                      Website
                    </label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      className={`block w-full p-3 border rounded-lg focus:ring-primary focus:border-primary transition ${errors.website ? "border-red-500" : "border-gray-300"
                        }`}
                      placeholder="https://example.com"
                    />
                    {errors.website && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.website}
                      </p>
                    )}
                  </div>

                  <OperatingHoursInput
                    formData={formData}
                    setFormData={setFormData}
                  />
                  {errors.hours_general && (
                    <p className="text-red-500 text-xs mt-1 mb-2">{errors.hours_general}</p>
                  )}
                  {errors.hours && (
                    <div className="mb-4">
                      {Object.entries(errors.hours).map(([day, error]) => (
                        <p key={day} className="text-red-500 text-xs">{day}: {error}</p>
                      ))}
                    </div>
                  )}

                  <div className="flex justify-end mt-6">
                    <button
                      type="button"
                      onClick={onCancel}
                      className="bg-gray-300 px-4 py-2 rounded-lg mr-2"
                      disabled={imageLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="bg-primary text-white px-4 py-2 rounded-lg flex items-center"
                      disabled={imageLoading}
                    >
                      {imageLoading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      )}
                      {item ? "Update" : "Save"}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
